import {Box, Text} from '@mantine/core';
import Head from 'next/head';
import Image from 'next/image';
import {useRouter} from 'next/router';
import {parseCookies} from 'nookies';
import type React from 'react';
import {TITLE_SUFFIX} from '../../../../config/constants';
import {GSSKT_HOST} from '../../../../config/constants/hosts';
import {
  SKILL_CHECK_MAIN_VISUAL_PC,
  SKILL_CHECK_MAIN_VISUAL_SP,
  SKILL_CHECK_OGP,
} from '../../../../config/constants/img';
import {useGetSkillCheckTop} from '../../../../core/repositories/skillCheckRepository';
import {validateCookie} from '../../../../core/usecases/auth/checkOauthTokenExistence';
import {useIsAppWebView} from '../../../../shared/hooks/useIsAppWebView';
import {useMediaQuery} from '../../../../shared/services/useMediaQuery';
import {CategoryCard} from './Presenter/CategoryCard';

type Props = Record<string, never>;

export const TopPage: React.FC<Props> = () => {
  const isWebView = useIsAppWebView();
  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);

  const router = useRouter();
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath)}`;
  const TITLE = 'スキルチェック';
  const {isPC} = useMediaQuery();
  const {data: skillCheckTopData} = useGetSkillCheckTop({});

  return (
    <>
      <Head>
        <title key="title">
          {TITLE}
          {TITLE_SUFFIX}
        </title>
        <meta
          key="og:title"
          property="og:title"
          content={TITLE + TITLE_SUFFIX}
        />
        <meta
          key="og:site_name"
          property="og:site_name"
          content={TITLE + TITLE_SUFFIX}
        />
        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta
          key="og:image"
          property="og:image"
          content={`${GSSKT_HOST}${SKILL_CHECK_OGP}`}
        />
        <meta
          key="description"
          name="description"
          content="エンジニア企業の求人情報、選考情報・先輩のエントリーシート・選考突破法、コーディング試験対策などの多様なイベント、エンジニア同士が集まる専用コミュニティなどを通して就活を支援します。"
        />
        <meta
          key="keywords"
          name="keywords"
          content="外資,就活,新卒,エンジニア,技術職,エンジニア就活サイト,外資IT企業,新卒エンジニア外資,外資系IT新卒採用,テクノロジー外資就職,外資系ソフトウェア開発,プログラミング求人外資,外資系エンジニアキャリア,外資系開発職就活,グローバルIT企業求人,外資系技術職,システムエンジニア,ソフトウェアエンジニア求人,フロントエンドエンジニア,バックエンドエンジニア,AIエンジニア,データサイエンティスト,セキュリティエンジニア,エンジニアコミュニティ"
        />
        <link rel="canonical" href={currentUrl} key="canonical" />
      </Head>

      <Box
        className={`flex flex-col  pc:w-[1200px] w-full ${isValid || isWebView ? '' : 'pc:pt-[64px] pt-[50px]'}`}
      >
        <Box
          component="h1"
          className="flex relative pc:h-[500px] pb-[160%] pc:pb-0"
        >
          {isPC !== undefined && (
            <Image
              fill
              style={{objectFit: 'cover'}}
              alt="スキルチェック"
              src={
                isPC ? SKILL_CHECK_MAIN_VISUAL_PC : SKILL_CHECK_MAIN_VISUAL_SP
              }
            />
          )}
        </Box>

        <Box className="flex flex-col mt-40 mb-24 item-center text-center">
          <Text className="font-bold text-xl">スキルチェック一覧</Text>
          <Text className="mt-8 text-md">
            1スキルにつき一日一回
            <br />
            チェックを受けることができます
          </Text>
        </Box>

        <Box className="flex pc:w-[1092px] flex-row flex-wrap gap-x-16 gap-y-16 pc:justify-start justify-center self-center">
          {skillCheckTopData?.ok &&
            skillCheckTopData?.value.categories.map((data) => (
              <CategoryCard
                key={data.id}
                id={data.id}
                name={data.name}
                genre={data.genre}
                lastResult={data.lastResult}
              />
            ))}
        </Box>
      </Box>
    </>
  );
};
